import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useGame } from '@/app/providers/GameContextProvider';
import { useGameModeSettingsContext } from '@/app/providers/GameModeSettingsProvider';
import { CustomModeSettings, CustomBaseMode } from '@/lib/types/common/GameModeAdvancedSettings';
import { GameMode } from '@/lib/types/common/GameMode';
import TextFFXIV from '@/app/components/TextFFXIV';
import { useGlobalPopup } from '@/app/providers/GlobalPopupProvider';
import RoundConfig from './Config/RoundConfig';
import TeamConfig from './Config/TeamConfig';
import ChallengeConfig from './Config/ChallengeConfig';
import ToggleFFXIV from '@/app/components/ToggleFFXIV';

// Base modes that can be selected in Custom mode
const BASE_MODES: { mode: CustomBaseMode; label: string }[] = [
    { mode: GameMode.CLASSIC, label: 'Classic' },
    { mode: GameMode.TIMED, label: 'Timed' },
    { mode: GameMode.FIGHT, label: 'Fight' },
    { mode: GameMode.ENDLESS, label: 'Endless' },
    { mode: GameMode.COOP, label: 'Coop' },
];

const CustomModeAdvancedSettings: React.FC = () => {
    const { t } = useTranslation();
    const { room, gameState } = useGame();
    const { showPopup } = useGlobalPopup();
    const { settings: contextSettings, updateSettings, isGameMaster } = useGameModeSettingsContext();

    // Cast to CustomModeSettings
    const settings = contextSettings as CustomModeSettings;

    const handleBaseModeChange = (baseMode: CustomBaseMode) => {
        if (!isGameMaster) {
            showPopup({ type: 'error', message: t('lobby.onlyGameMasterCan') });
            return;
        }

        // Update settings based on the new base mode
        const newSettings: Partial<CustomModeSettings> = {
            baseMode,
        };

        // Adjust settings based on base mode
        if (baseMode === GameMode.CLASSIC) {
            newSettings.maxRounds = 5;
            newSettings.timerSeconds = null;
        } else if (baseMode === GameMode.ENDLESS) {
            newSettings.maxRounds = -1;
            newSettings.timerSeconds = null;
        } else if (baseMode === GameMode.FIGHT) {
            newSettings.maxRounds = -1;
            newSettings.minPlayers = 2;
            if (settings.numberOfTeams && settings.numberOfTeams < 2) {
                newSettings.numberOfTeams = 2;
            }
            // Restore timer if it was null from Endless
            if (settings.timerSeconds === null) {
                newSettings.timerSeconds = 60;
            }
        } else if (baseMode === GameMode.COOP) {
            newSettings.minPlayers = 2;
            // Restore rounds and timer if coming from Fight/Endless
            if (settings.maxRounds === -1) {
                newSettings.maxRounds = 5;
            }
            if (settings.timerSeconds === null) {
                newSettings.timerSeconds = 60;
            }
        } else if (baseMode === GameMode.TIMED) {
            // Restore rounds and timer if coming from Fight/Endless
            if (settings.maxRounds === -1) {
                newSettings.maxRounds = 5;
            }
            if (settings.timerSeconds === null) {
                newSettings.timerSeconds = 60;
            }
            newSettings.minPlayers = 1;
        } else if (baseMode === GameMode.CLASSIC) {
            // Restore rounds and timer if coming from Fight/Endless
            if (settings.maxRounds === -1) {
                newSettings.maxRounds = 5;
            }
            if (settings.timerSeconds === null) {
                newSettings.timerSeconds = 60;
            }
            newSettings.minPlayers = 1;
        }

        updateSettings(newSettings);
    };

    if (!room || !gameState) return null;

    return (
        <div className={`flex flex-col gap-4 ${!isGameMaster ? 'opacity-50' : ''}`}>
            <TextFFXIV text={t('game.config.advancedSettings.title')} className="text-center" />
            {/* Base Mode Selector */}
            <div className={`flex gap-7 items-center`}>
                <div className="text-[#EEE1C5] text-shadow-border shadow-black text-lg">
                    {t('game.modes.custom.baseMode')}
                </div>
                <div className="flex gap-7">
                    {BASE_MODES.map(({ mode }) => (
                        <ToggleFFXIV
                            key={mode}
                            value={settings.baseMode === mode}
                            onChange={() => handleBaseModeChange(mode)}
                        >
                            <div className="flex items-center gap-1 text-shadow-border shadow-black">
                                {t(`game.modes.${mode}.name`)}
                            </div>
                        </ToggleFFXIV>
                    ))}
                </div>
            </div>

            <div className="flex flex-col gap-4">
                {settings.baseMode === GameMode.CLASSIC && (
                    <RoundConfig showTimer={false} />
                )}
                {settings.baseMode === GameMode.TIMED && (
                    <RoundConfig />
                )}
                {settings.baseMode === GameMode.FIGHT && (
                    <>
                        <RoundConfig
                            showRounds={false}
                            forceTimer={false}
                        />
                        <TeamConfig />
                    </>
                )}
                {settings.baseMode === GameMode.ENDLESS && (
                    <RoundConfig
                        showRounds={false}
                        forceTimer={false}
                    />
                )}
                {settings.baseMode === GameMode.COOP && (
                    <>
                        <RoundConfig
                            forceTimer={false}
                        />
                        <TeamConfig
                            showMaxHP={false}
                            showDamageMultiplier={false}
                        />
                    </>
                )}
                <ChallengeConfig />
            </div>
        </div>
    );
};

export default CustomModeAdvancedSettings;
